import jsPDF from 'jspdf';
import { Invoice } from '../types/invoice';

const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
};

const formatCurrency = (amount: number) => `BD ${amount.toFixed(3)}`;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const isAlmanaratainTemplate = (invoice: Invoice) =>
  (invoice.template?.name || '').toLowerCase().includes('almanaratain');

export const generateInvoicePDF = (invoice: Invoice) => {
  const layoutType = invoice.template?.layoutType || 'modern';

  if (layoutType === 'classic') {
    generateClassicInvoicePDF(invoice);
    return;
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ── Color palette ──────────────────────────────────────────────────────────
  const colorScheme = invoice.template?.colorScheme;
  const gold:     [number, number, number] = colorScheme?.primary ? hexToRgb(colorScheme.primary) : [192, 155, 86];
  const darkText: [number, number, number] = colorScheme?.text ? hexToRgb(colorScheme.text) : [25,  25,  25];
  const grayText: [number, number, number] = colorScheme?.secondary ? hexToRgb(colorScheme.secondary) : [100, 100, 100];
  const notesBg:  [number, number, number] = colorScheme?.background ? hexToRgb(colorScheme.background) : [246, 246, 244];

  const drawGoldLine = (y: number) => {
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.6);
    doc.line(15, y, pageWidth - 15, y);
  };

  // ── LOGO (top-right) ────────────────────────────────────────────────────────
  const logoSize = 40;
  const logoX    = pageWidth - 15 - logoSize;
  const logoY    = 5;

  const logoSource = invoice.template?.templateLogo || invoice.company?.logo;
  const logoBase64 = invoice.template?.logoBase64;

  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', logoX, logoY, logoSize, logoSize); } catch { /* skip */ }
  } else if (logoSource) {
    try {
      const url = logoSource.startsWith('http')
        ? logoSource
        : `${window.location.origin.replace(':3000', ':5002')}/${logoSource}`;
      doc.addImage(url, 'PNG', logoX, logoY, logoSize, logoSize);
    } catch { /* skip */ }
  }

  // ── INVOICE TITLE (top-left) ──────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(32);
  doc.setTextColor(...gold);
  doc.text('INVOICE', 15, 22);

  // Gold separator just below the INVOICE title
  const sep1Y = 31;
  drawGoldLine(sep1Y);

  // ── INVOICE META (left) + BILL TO (right) ─────────────────────────────────
  let yPos = sep1Y + 8;

  // Left side: Invoice meta
  doc.setFontSize(9);
  const labelX = 15;
  const valueX = 58;

  const metaRows = [
    { label: 'Invoice #',     value: invoice.invoiceNumber },
    { label: 'Invoice Date',  value: formatDate(invoice.issueDate) },
    { label: 'Due Date',      value: formatDate(invoice.dueDate) },
  ];

  if (isAlmanaratainTemplate(invoice) && invoice.job) {
    metaRows.push({ label: 'Job #',   value: invoice.job.jobNumber });
    metaRows.push({ label: 'Job Name', value: invoice.job.jobName });
  }

  let leftY = yPos;
  metaRows.forEach((row) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);
    doc.text(row.label, labelX, leftY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    const valueLines = doc.splitTextToSize(row.value, 60);
    doc.text(valueLines, valueX, leftY);
    leftY += 6 * valueLines.length;
  });

  // Right side: Bill To
  const rightX = 120;
  let rightY = yPos;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...darkText);
  doc.text('BILL TO:', rightX, rightY);
  rightY += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(invoice.customer?.name || 'Customer', rightX, rightY);
  rightY += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...grayText);

  const addrParts: string[] = [];
  if (invoice.customer?.address) addrParts.push(invoice.customer.address);
  const locParts = [
    invoice.customer?.city,
    invoice.customer?.state,
    invoice.customer?.postalCode,
    invoice.customer?.country,
  ].filter(Boolean);
  if (locParts.length) addrParts.push(locParts.join(', '));

  if (addrParts.length) {
    const addrText = addrParts.join(', ');
    const addrLines = doc.splitTextToSize(addrText, pageWidth - rightX - 15);
    doc.text(addrLines, rightX, rightY);
    rightY += addrLines.length * 5;
  }

  if (invoice.customer?.email || invoice.customer?.phone) {
    const contact = [invoice.customer?.email, invoice.customer?.phone].filter(Boolean).join(' | ');
    doc.text(contact, rightX, rightY);
    rightY += 5;
  }

  // Advance yPos past whichever column is taller
  yPos = Math.max(leftY, rightY) + 4;
  drawGoldLine(yPos);

  // ── ITEMS TABLE ─────────────────────────────────────────────────────────────
  yPos += 8;

  const tableLeft  = 15;
  const tableRight = pageWidth - 15;
  const tableW     = tableRight - tableLeft; // 180mm
  const headerH    = 10;

  // Column widths (total = 180mm)
  const colW = {
    desc:   85,
    qty:    18,
    rate:   28,
    tax:    18,
    amount: 31,
  };

  const drawTableHeader = (y: number) => {
    doc.setFillColor(...gold);
    doc.rect(tableLeft, y, tableW, headerH, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(255, 255, 255);

    doc.text('SUMMARY OF SERVICES', tableLeft + 3, y + 6.5);
    let hx = tableLeft + colW.desc;
    doc.text('QTY',    hx + 3, y + 6.5);
    hx += colW.qty;
    doc.text('RATE',   hx + 3, y + 6.5);
    hx += colW.rate;
    doc.text('TAX',    hx + 3, y + 6.5);
    doc.text('AMOUNT', tableRight - 3, y + 6.5, { align: 'right' });
  };

  let tableStartY = yPos;
  drawTableHeader(yPos);
  yPos += headerH;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  invoice.items?.forEach((item, index) => {
    // Pre-calculate row height to check for page break
    const descPreview = doc.splitTextToSize(item.description, colW.desc - 5);
    let previewH = descPreview.length * 5;
    if (item.serialNumbers && item.serialNumbers.length > 0) {
      previewH += item.serialNumbers.length * 3.5 + 4;
    }
    const rowH = Math.max(12, previewH + 6);

    if (yPos + rowH > pageHeight - 65) {
      // Close table on current page
      doc.setDrawColor(...gold);
      doc.setLineWidth(0.6);
      doc.rect(tableLeft, tableStartY, tableW, yPos - tableStartY);

      doc.addPage();
      yPos = 20;
      tableStartY = yPos;
      drawTableHeader(yPos);
      yPos += headerH;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
    }

    const rowStartY = yPos + 5;
    const descLines = doc.splitTextToSize(item.description, colW.desc - 5);
    doc.setTextColor(...darkText);
    doc.text(descLines, tableLeft + 3, rowStartY);

    let descH = descLines.length * 5;

    // Serial numbers below description
    if (item.serialNumbers && item.serialNumbers.length > 0) {
      let sy = rowStartY + descLines.length * 5;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...grayText);
      item.serialNumbers.forEach((s) => {
        doc.text(`• ${s}`, tableLeft + 3, sy);
        sy += 3.5;
      });
      descH = sy - rowStartY;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkText);
    }

    let cx = tableLeft + colW.desc + 3;
    doc.text(item.quantity.toString(), cx, rowStartY);
    cx += colW.qty;
    doc.text(formatCurrency(item.rate), cx, rowStartY);
    cx += colW.rate;
    doc.text(item.taxRate ? `${item.taxRate}%` : '–', cx, rowStartY);
    doc.text(formatCurrency(item.amount), tableRight - 3, rowStartY, { align: 'right' });

    yPos += Math.max(12, descH + 6);

    // Row separator (light gray, except after last row)
    if (index < (invoice.items?.length ?? 0) - 1) {
      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.2);
      doc.line(tableLeft, yPos, tableRight, yPos);
    }
  });

  // Gold outer border around entire table
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.6);
  doc.rect(tableLeft, tableStartY, tableW, yPos - tableStartY);

  // ── TOTALS ──────────────────────────────────────────────────────────────────
  yPos += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...darkText);
  doc.text('TOTALS', 15, yPos);

  yPos += 7;
  doc.setFontSize(9);

  // • Subtotal
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text('•  Subtotal', 15, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(invoice.subtotal), 65, yPos);

  yPos += 6;

  // • Tax
  const taxLabel = invoice.taxAmount === 0 ? 'Tax (0%):' : 'Tax:';
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text(`•  ${taxLabel}`, 15, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gold);
  doc.text(formatCurrency(invoice.taxAmount), 65, yPos);
  doc.setTextColor(...darkText);

  // ── NOTES & PAYMENT ─────────────────────────────────────────────────────────
  yPos += 12;

  if (yPos > pageHeight - 50) {
    doc.addPage();
    yPos = 20;
  }

  const notesText = invoice.notes
    ? invoice.notes
    : 'Thank you for your business! Please make payment by the due date mentioned above. For inquiries, contact us at the details provided in the header.';

  const notesBoxW  = pageWidth - 30;
  const goldBarW   = 4;
  const textMaxW   = notesBoxW - goldBarW - 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const notesLines = doc.splitTextToSize(notesText, textMaxW);
  const notesBoxH  = 10 + notesLines.length * 4.5 + 10;

  // Background
  doc.setFillColor(...notesBg);
  doc.rect(15, yPos, notesBoxW, notesBoxH, 'F');

  // Gold left bar
  doc.setFillColor(...gold);
  doc.rect(15, yPos, goldBarW, notesBoxH, 'F');

  // Heading
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...darkText);
  doc.text('NOTES & PAYMENT', 15 + goldBarW + 4, yPos + 8);

  // Notes text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...grayText);
  doc.text(notesLines, 15 + goldBarW + 4, yPos + 15);

  // Amount Due
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  if (invoice.status === 'PAID') {
    doc.setTextColor(...darkText);
    doc.text('Amount Due:  ', 15 + goldBarW + 4, yPos + notesBoxH - 3);
    const labelWidth = doc.getTextWidth('Amount Due:  ');
    doc.setTextColor(34, 139, 34);
    doc.text('PAID', 15 + goldBarW + 4 + labelWidth, yPos + notesBoxH - 3);
  } else {
    doc.setTextColor(...darkText);
    doc.text(`Amount Due:  ${formatCurrency(invoice.remainingAmount)}`, 15 + goldBarW + 4, yPos + notesBoxH - 3);
  }

  // ── FOOTER (company info) ──────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    let footY = pageHeight - 22;

    // Tagline (gold italic) — not shown for the Almanaratain template
    if (!isAlmanaratainTemplate(invoice)) {
      doc.setFont('helvetica', 'bolditalic');
      doc.setFontSize(8);
      doc.setTextColor(...gold);
      doc.text('Elevate Your Space With Soothing Scents', pageWidth / 2, footY, { align: 'center' });
      footY += 4;
    }

    // Address line
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...grayText);

    const footerParts: string[] = [];
    if (invoice.company?.city || invoice.company?.country) {
      footerParts.push([invoice.company?.city, invoice.company?.country].filter(Boolean).join(', '));
    }
    if (invoice.company?.address) footerParts.push(invoice.company.address);
    if (footerParts.length) {
      doc.text(footerParts.join('  |  '), pageWidth / 2, footY, { align: 'center' });
      footY += 4;
    }

    // Phone | Email
    if (invoice.company?.phone || invoice.company?.email) {
      const contact = [invoice.company?.phone, invoice.company?.email].filter(Boolean).join('  |  ');
      doc.text(contact, pageWidth / 2, footY, { align: 'center' });
    }
  }

  // ── SAVE ────────────────────────────────────────────────────────────────────
  const fileName = `Invoice_${invoice.invoiceNumber}_${invoice.customer?.name || 'Customer'}.pdf`;
  doc.save(fileName);
};

// ── CLASSIC LAYOUT ─────────────────────────────────────────────────────────────
// Navy-and-slate professional design: left logo/company block, right invoice
// meta, boxed "Bill To", full-width dark table header, filled Amount Due bar.
const generateClassicInvoicePDF = (invoice: Invoice) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  const colorScheme = invoice.template?.colorScheme;
  const navy:  [number, number, number] = colorScheme?.primary    ? hexToRgb(colorScheme.primary)    : [30, 58, 95];
  const text:  [number, number, number] = colorScheme?.text       ? hexToRgb(colorScheme.text)       : [31, 41, 55];
  const muted: [number, number, number] = colorScheme?.secondary  ? hexToRgb(colorScheme.secondary)  : [100, 116, 139];
  const boxBg: [number, number, number] = colorScheme?.background ? hexToRgb(colorScheme.background) : [248, 250, 252];

  const companyName = invoice.template?.templateCompanyName || invoice.company?.name || 'Your Company';

  // ── HEADER: logo + company name (left), INVOICE title + meta (right) ───────
  const logoSize = 24;
  const logoX = margin;
  const logoY = 10;
  let textX = margin;

  const logoSource = invoice.template?.templateLogo || invoice.company?.logo;
  const logoBase64 = invoice.template?.logoBase64;
  let hasLogo = false;

  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', logoX, logoY, logoSize, logoSize); hasLogo = true; } catch { /* skip */ }
  } else if (logoSource) {
    try {
      const url = logoSource.startsWith('http')
        ? logoSource
        : `${window.location.origin.replace(':3000', ':5002')}/${logoSource}`;
      doc.addImage(url, 'PNG', logoX, logoY, logoSize, logoSize);
      hasLogo = true;
    } catch { /* skip */ }
  }
  if (hasLogo) textX = logoX + logoSize + 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...navy);
  const nameLines = doc.splitTextToSize(companyName.toUpperCase(), pageWidth - textX - 75);
  let nameY = logoY + 8;
  nameLines.forEach((line: string) => {
    doc.text(line, textX, nameY);
    nameY += 6;
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(...navy);
  doc.text('INVOICE', pageWidth - margin, 22, { align: 'right' });

  const metaRows = [
    { label: 'Invoice #:',    value: invoice.invoiceNumber },
    { label: 'Invoice Date:', value: formatDate(invoice.issueDate) },
    { label: 'Due Date:',     value: formatDate(invoice.dueDate) },
  ];

  if (isAlmanaratainTemplate(invoice) && invoice.job) {
    metaRows.push({ label: 'Job #:',    value: invoice.job.jobNumber });
    metaRows.push({ label: 'Job Name:', value: invoice.job.jobName });
  }

  let metaY = 30;
  doc.setFontSize(9);
  metaRows.forEach((row) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...muted);
    doc.text(row.label, pageWidth - margin - 40, metaY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...text);
    const valueLines = doc.splitTextToSize(row.value, 40);
    doc.text(valueLines, pageWidth - margin, metaY, { align: 'right' });
    metaY += 6 * valueLines.length;
  });

  let yPos = Math.max(nameY, metaY, logoY + logoSize) + 6;
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.6);
  doc.line(margin, yPos, pageWidth - margin, yPos);

  // ── BILL TO box ──────────────────────────────────────────────────────────────
  yPos += 8;
  const barW = 3;
  const boxW = pageWidth - margin * 2;
  const contentW = boxW - barW - 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const addrParts: string[] = [];
  if (invoice.customer?.address) addrParts.push(invoice.customer.address);
  const locParts = [
    invoice.customer?.city,
    invoice.customer?.state,
    invoice.customer?.postalCode,
    invoice.customer?.country,
  ].filter(Boolean);
  if (locParts.length) addrParts.push(locParts.join(', '));
  const addrLines = addrParts.length ? doc.splitTextToSize(addrParts.join(', '), contentW) : [];
  const hasContact = Boolean(invoice.customer?.email || invoice.customer?.phone);

  const boxH = 8 + 6 + addrLines.length * 4.5 + (hasContact ? 4.5 : 0) + 5;

  doc.setFillColor(...boxBg);
  doc.rect(margin, yPos, boxW, boxH, 'F');
  doc.setFillColor(...navy);
  doc.rect(margin, yPos, barW, boxH, 'F');

  const contentX = margin + barW + 7;
  let boxY = yPos + 7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text('BILL TO', contentX, boxY);
  boxY += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...text);
  doc.text(invoice.customer?.name || 'Customer', contentX, boxY);
  boxY += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...muted);
  if (addrLines.length) {
    doc.text(addrLines, contentX, boxY);
    boxY += addrLines.length * 4.5;
  }
  if (hasContact) {
    const contact = [invoice.customer?.email, invoice.customer?.phone].filter(Boolean).join(' | ');
    doc.text(contact, contentX, boxY);
  }

  yPos += boxH + 8;

  // ── ITEMS TABLE (no tax column) ─────────────────────────────────────────────
  const tableLeft  = margin;
  const tableRight = pageWidth - margin;
  const tableW     = tableRight - tableLeft;
  const headerH    = 10;
  const colW = { desc: 95, qty: 22, rate: 30, amount: 33 };

  const drawTableHeader = (y: number) => {
    doc.setFillColor(...navy);
    doc.rect(tableLeft, y, tableW, headerH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(255, 255, 255);
    doc.text('SUMMARY OF SERVICES', tableLeft + 3, y + 6.5);
    let hx = tableLeft + colW.desc;
    doc.text('QTY', hx + 3, y + 6.5);
    hx += colW.qty;
    doc.text('RATE', hx + 3, y + 6.5);
    doc.text('AMOUNT', tableRight - 3, y + 6.5, { align: 'right' });
  };

  drawTableHeader(yPos);
  yPos += headerH;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  invoice.items?.forEach((item) => {
    const descPreview = doc.splitTextToSize(item.description, colW.desc - 5);
    let previewH = descPreview.length * 5;
    if (item.serialNumbers && item.serialNumbers.length > 0) {
      previewH += item.serialNumbers.length * 3.5 + 4;
    }
    const rowH = Math.max(12, previewH + 6);

    if (yPos + rowH > pageHeight - 65) {
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(tableLeft, yPos, tableRight, yPos);

      doc.addPage();
      yPos = 20;
      drawTableHeader(yPos);
      yPos += headerH;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
    }

    const rowStartY = yPos + 5;
    const descLines = doc.splitTextToSize(item.description, colW.desc - 5);
    doc.setTextColor(...text);
    doc.text(descLines, tableLeft + 3, rowStartY);

    let descH = descLines.length * 5;

    if (item.serialNumbers && item.serialNumbers.length > 0) {
      let sy = rowStartY + descLines.length * 5;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...muted);
      item.serialNumbers.forEach((s) => {
        doc.text(`• ${s}`, tableLeft + 3, sy);
        sy += 3.5;
      });
      descH = sy - rowStartY;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...text);
    }

    let cx = tableLeft + colW.desc + 3;
    doc.text(item.quantity.toString(), cx, rowStartY);
    cx += colW.qty;
    doc.text(formatCurrency(item.rate), cx, rowStartY);
    doc.text(formatCurrency(item.amount), tableRight - 3, rowStartY, { align: 'right' });

    yPos += Math.max(12, descH + 6);

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(tableLeft, yPos, tableRight, yPos);
  });

  // ── TOTALS + AMOUNT DUE bar ──────────────────────────────────────────────────
  yPos += 8;
  const summaryLeft = pageWidth - margin - 95;
  const summaryRight = pageWidth - margin;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...muted);
  doc.text('Subtotal', summaryLeft, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...text);
  doc.text(formatCurrency(invoice.subtotal), summaryRight, yPos, { align: 'right' });

  yPos += 6.5;
  const taxLabel = invoice.taxAmount === 0 ? 'Tax (0%)' : 'Tax';
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...muted);
  doc.text(taxLabel, summaryLeft, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...text);
  doc.text(formatCurrency(invoice.taxAmount), summaryRight, yPos, { align: 'right' });

  yPos += 6;
  const barH = 11;
  doc.setFillColor(...navy);
  doc.rect(summaryLeft, yPos, summaryRight - summaryLeft, barH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(255, 255, 255);
  doc.text('Amount Due', summaryLeft + 4, yPos + barH / 2 + 3.5);
  if (invoice.status === 'PAID') {
    doc.setTextColor(167, 243, 208);
    doc.text('PAID', summaryRight - 4, yPos + barH / 2 + 3.5, { align: 'right' });
  } else {
    doc.text(formatCurrency(invoice.remainingAmount), summaryRight - 4, yPos + barH / 2 + 3.5, { align: 'right' });
  }

  // ── NOTES & PAYMENT DETAILS ──────────────────────────────────────────────────
  yPos += barH + 10;

  if (yPos > pageHeight - 50) {
    doc.addPage();
    yPos = 20;
  }

  const notesText = invoice.notes
    ? invoice.notes
    : 'Thank you for your business! Please make payment by the due date mentioned above. For inquiries, contact us at the details provided in the header.';

  const notesBoxW = pageWidth - margin * 2;
  const notesPad = 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const notesLines = doc.splitTextToSize(notesText, notesBoxW - notesPad * 2);
  const notesBoxH = 10 + notesLines.length * 4.5 + 6;

  doc.setFillColor(...boxBg);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.rect(margin, yPos, notesBoxW, notesBoxH, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...text);
  doc.text('NOTES & PAYMENT DETAILS', margin + notesPad, yPos + 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...muted);
  doc.text(notesLines, margin + notesPad, yPos + 14.5);

  // ── FOOTER ────────────────────────────────────────────────────────────────────
  const footerContactParts: string[] = [];
  if (invoice.company?.city || invoice.company?.country) {
    footerContactParts.push([invoice.company?.city, invoice.company?.country].filter(Boolean).join(', '));
  }
  if (invoice.company?.address) footerContactParts.push(invoice.company.address);
  const footerContactLine = footerContactParts.join('  |  ');
  const footerReachLine = [invoice.company?.phone, invoice.company?.email].filter(Boolean).join('  |  ');

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 22, pageWidth - margin, pageHeight - 22);

    let footY = pageHeight - 17;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...text);
    doc.text(companyName, pageWidth / 2, footY, { align: 'center' });
    footY += 4.5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    if (footerContactLine) {
      doc.text(footerContactLine, pageWidth / 2, footY, { align: 'center' });
      footY += 4;
    }
    if (footerReachLine) {
      doc.text(footerReachLine, pageWidth / 2, footY, { align: 'center' });
    }
  }

  const fileName = `Invoice_${invoice.invoiceNumber}_${invoice.customer?.name || 'Customer'}.pdf`;
  doc.save(fileName);
};
